// /src/pages/orders/OrdersUnitsHeatmapByState.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card } from "../../components/card/card.jsx";

import USStateUnitsChoroplethMap from "../../components/maps/USStateUnitsChoroplethMap";

import { fetchAnalyticsProductOptions, fetchOrdersUnitsHeatmapByState } from "../../actions/orders_actions";

// NEW: modal (keeps everything else unchanged)
import { Modal, Button } from "react-bootstrap";

const yyyyMmDd = (d) => new Date(d).toISOString().slice(0, 10);
const todayStr = () => yyyyMmDd(new Date());
const threeMonthsAgoStr = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return yyyyMmDd(d);
};

const getThemeColorFromCss = () =>
  (typeof document !== "undefined"
    ? getComputedStyle(document.body).getPropertyValue("--bs-theme").trim()
    : "") || "#0d6efd";

const OrdersUnitsHeatmapByStateMap = () => {
  const dispatch = useDispatch();

  // theme color reactive
  const [themeColor, setThemeColor] = useState(getThemeColorFromCss);
  useEffect(() => {
    const update = () => setThemeColor(getThemeColorFromCss());
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.body, { attributes: true, attributeFilter: ["class", "style"] });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class", "style"] });
    return () => obs.disconnect();
  }, []);

  const [heatmapMetric, setHeatmapMetric] = useState("units"); // "units" | "amount"

  // default last 3 months
  const [startDate, setStartDate] = useState(threeMonthsAgoStr);
  const [endDate, setEndDate] = useState(todayStr);

  // product filter
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [appliedProductIds, setAppliedProductIds] = useState([]);

  const ordersState = useSelector((state) => state.orders || {});
  const {
    analytics_product_options,
    loading_analytics_product_options,
    analytics_product_options_error,

    orders_units_heatmap_by_state,
    loading_orders_units_heatmap_by_state,
    orders_units_heatmap_by_state_error,
  } = ordersState;

  useEffect(() => {
    dispatch(fetchAnalyticsProductOptions());
  }, [dispatch]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest?.("#productsMultiSelectDropdown")) setProductsDropdownOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const normalizeIdsKey = useCallback((ids) => {
    return (Array.isArray(ids) ? ids : [])
      .map((x) => String(x).trim())
      .filter(Boolean)
      .sort()
      .join(",");
  }, []);

  const toggleProductId = useCallback((id) => {
    const sid = String(id);
    setSelectedProductIds((prev) => (prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]));
  }, []);

  const clearSelectedProducts = useCallback(() => setSelectedProductIds([]), []);

  const selectedProductsLabel = useMemo(() => {
    if (!selectedProductIds.length) return "All products";
    const labelById = new Map((analytics_product_options || []).map((o) => [String(o.value), o.label]));
    const labels = selectedProductIds.map((id) => labelById.get(String(id)) || id);
    if (labels.length <= 2) return labels.join(", ");
    return `${labels[0]}, ${labels[1]} +${labels.length - 2}`;
  }, [selectedProductIds, analytics_product_options]);

  const hasSelectionChanges = useMemo(() => {
    return normalizeIdsKey(selectedProductIds) !== normalizeIdsKey(appliedProductIds);
  }, [selectedProductIds, appliedProductIds, normalizeIdsKey]);

  const buildUpcsFromProductIds = useCallback(
    (productIds) => {
      const byId = new Map((analytics_product_options || []).map((o) => [String(o.value), o]));
      const objs = (productIds || []).map((id) => byId.get(String(id))).filter(Boolean);
      const upcs = objs.flatMap((o) => (Array.isArray(o.upcs) ? o.upcs : []));
      return Array.from(new Set(upcs.map(String).map((s) => s.trim()).filter(Boolean)));
    },
    [analytics_product_options]
  );

  // Shaker Pro orders are merged into the US heatmap by the backend.
  const doFetch = useCallback(
    (sd, ed, upcs) => dispatch(fetchOrdersUnitsHeatmapByState(sd, ed, upcs)),
    [dispatch]
  );

  // initial fetch (default last 3 months, all products)
  useEffect(() => {
    doFetch(startDate, endDate, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchClick = useCallback(() => {
    setAppliedProductIds(selectedProductIds);
    const upcs = buildUpcsFromProductIds(selectedProductIds);
    doFetch(startDate, endDate, upcs);
    setProductsDropdownOpen(false);
  }, [selectedProductIds, buildUpcsFromProductIds, doFetch, startDate, endDate]);

  const appliedUpcs = useMemo(
    () => buildUpcsFromProductIds(appliedProductIds),
    [appliedProductIds, buildUpcsFromProductIds]
  );

  const CONTROL_HEIGHT = 38;
  const CONTROL_RADIUS = 6;

  // =========================
  // NEW: Drilldown modal state
  // =========================
  const [showStateModal, setShowStateModal] = useState(false);
  const [selectedState, setSelectedState] = useState(null);

  const closeStateModal = useCallback(() => {
    setShowStateModal(false);
    setSelectedState(null);
  }, []);

  const handleStateClick = useCallback((stateAbbr /*, regionCode, row */) => {
    if (!stateAbbr) return;
    setSelectedState(String(stateAbbr).toUpperCase());
    setShowStateModal(true);
  }, []);

  // Helper: format $ consistently (keep lightweight; no dependency changes)
  const formatMoney = useCallback((v) => {
    const n = Number(v || 0);
    if (!Number.isFinite(n)) return "$0.00";
    return `$${n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  }, []);

  // Find the clicked state's row (which now contains cities[])
  const selectedStateRow = useMemo(() => {
    const st = String(selectedState || "").trim().toUpperCase();
    if (!st) return null;
    return (Array.isArray(orders_units_heatmap_by_state) ? orders_units_heatmap_by_state : []).find(
      (r) => String(r?.state || "").trim().toUpperCase() === st
    );
  }, [orders_units_heatmap_by_state, selectedState]);

  const sortedCities = useMemo(() => {
    const cities = selectedStateRow?.cities;
    const arr = Array.isArray(cities) ? [...cities] : [];
    const key = heatmapMetric === "amount" ? "totalAmount" : "totalUnits";
    arr.sort((a, b) => Number(b?.[key] || 0) - Number(a?.[key] || 0));
    return arr;
  }, [selectedStateRow, heatmapMetric]);

  // Auto-close modal if data gets cleared (e.g., new search returns empty)
  useEffect(() => {
    if (showStateModal && selectedState && !selectedStateRow) {
      // keep modal open if you prefer; this just avoids stale state when selection changes dramatically
      // closeStateModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders_units_heatmap_by_state]);

  return (
    <div>
      {/* Top header + filters */}
      <div className="mb-3">
        {/* Bootstrap row like the HW page: wraps naturally, no overflow */}
        <div className="row g-3 align-items-start">
          {/* Left */}
          <div className="col-12 col-xl-auto">
            <div style={{ minWidth: 320 }}>
              <ul className="breadcrumb mb-1">
                <h6>
                  <li className="breadcrumb-item active">PAGES /</li>
                </h6>
                <h6>
                  <li className="breadcrumb-item">&nbsp;HEATMAP BY STATE</li>
                </h6>
              </ul>
              <h1 className="page-header mb-0">HEATMAP BY STATE</h1>
            </div>
          </div>

          {/* Center filters */}
          <div className="col-12 col-xl">
            {/* Keep it centered and “desktop-looking” without fixed widths */}
            <div className="mx-auto" style={{ maxWidth: 980 }}>
              {/* Labels */}
              <div className="row g-3">
                <div className="col-12 col-lg-6">
                  <label className="form-label mb-0">Products</label>
                </div>
                <div className="col-6 col-lg-2">
                  <label className="form-label mb-0">Date From</label>
                </div>
                <div className="col-6 col-lg-2">
                  <label className="form-label mb-0">Date To</label>
                </div>
                <div className="col-12 col-lg-2" />
              </div>

              {/* Controls */}
              <div className="row g-3 align-items-start">
                <div className="col-12 col-lg-6">
                  <div id="productsMultiSelectDropdown" className="position-relative">
                    <div className="dropdown">
                      <button
                        type="button"
                        className="form-control text-start d-flex align-items-center justify-content-between"
                        onClick={() => setProductsDropdownOpen((v) => !v)}
                        disabled={loading_analytics_product_options}
                        aria-expanded={productsDropdownOpen}
                        style={{
                          cursor: "pointer",
                          height: CONTROL_HEIGHT,
                          minHeight: CONTROL_HEIGHT,
                          display: "flex",
                          alignItems: "center",
                          borderRadius: CONTROL_RADIUS,
                        }}
                      >
                        <span className="text-truncate">
                          {loading_analytics_product_options ? "Loading…" : selectedProductsLabel}
                        </span>
                        <i className={`fa fa-chevron-${productsDropdownOpen ? "up" : "down"} ms-2`} />
                      </button>

                      {productsDropdownOpen && (
                        <div
                          className="dropdown-menu show p-2"
                          style={{ width: "100%", maxHeight: 280, overflowY: "auto", zIndex: 3000 }}
                        >
                          <div className="d-flex align-items-center justify-content-between px-2 pb-2">
                            <div className="small text-muted">
                              {selectedProductIds.length ? `${selectedProductIds.length} selected` : "None selected"}
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                clearSelectedProducts();
                              }}
                              disabled={!selectedProductIds.length}
                            >
                              Clear
                            </button>
                          </div>

                          <div className="dropdown-divider my-1" />

                          {analytics_product_options_error ? (
                            <div className="text-danger small px-2 py-2">{analytics_product_options_error}</div>
                          ) : (analytics_product_options || []).length === 0 ? (
                            <div className="text-muted small px-2 py-2">No products found</div>
                          ) : (
                            (analytics_product_options || []).map((opt) => {
                              const id = String(opt.value);
                              const checked = selectedProductIds.includes(id);

                              return (
                                <div
                                  key={id}
                                  role="menuitemcheckbox"
                                  aria-checked={checked}
                                  className="dropdown-item d-flex align-items-center gap-2"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleProductId(id);
                                  }}
                                  style={{ cursor: "pointer", userSelect: "none" }}
                                >
                                  <span
                                    aria-hidden="true"
                                    style={{
                                      width: 16,
                                      height: 16,
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      borderRadius: 3,
                                      border: `1px solid ${checked ? themeColor : "rgba(255,255,255,0.35)"}`,
                                      background: checked ? themeColor : "transparent",
                                      boxShadow: checked ? `0 0 0 2px ${themeColor}33` : "none",
                                      flex: "0 0 auto",
                                    }}
                                  >
                                    {checked ? <i className="fa fa-check" style={{ color: "#fff", fontSize: 11 }} /> : null}
                                  </span>
                                  <span className="flex-grow-1">{opt.label}</span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Helper row under products */}
                  <div className="mt-1">
                    {hasSelectionChanges ? (
                      <div className="text-warning small">Selection changed — press Search to apply</div>
                    ) : (
                      <div className="text-muted small">&nbsp;</div>
                    )}
                  </div>
                </div>

                <div className="col-6 col-lg-2">
                  <input
                    className="form-control"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{
                      height: CONTROL_HEIGHT,
                      minHeight: CONTROL_HEIGHT,
                      borderRadius: CONTROL_RADIUS,
                    }}
                  />
                  <div className="text-muted small mt-1">&nbsp;</div>
                </div>

                <div className="col-6 col-lg-2">
                  <input
                    className="form-control"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{
                      height: CONTROL_HEIGHT,
                      minHeight: CONTROL_HEIGHT,
                      borderRadius: CONTROL_RADIUS,
                    }}
                  />
                  <div className="text-muted small mt-1">&nbsp;</div>
                </div>

                <div className="col-12 col-lg-2 d-flex align-items-start">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleSearchClick}
                    disabled={!startDate || !endDate}
                    style={{
                      height: CONTROL_HEIGHT,
                      width: 44,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: CONTROL_RADIUS,
                    }}
                    title="Search"
                  >
                    <i className="fas fa-search" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right spacer (kept only on xl so it won't force overflow on small) */}
          <div className="d-none d-xl-block col-xl-auto">
            <div style={{ minWidth: 120 }} />
          </div>
        </div>
      </div>

      {/* Map Card */}
      <Card className="mb-4">
        <div className="px-4 pt-3 pb-2 d-flex justify-content-between align-items-start">
          <div>
            <h4 className="mb-1">US {heatmapMetric === "amount" ? "Amounts" : "Units"} Heatmap</h4>

            <div className="btn-group btn-group-sm mt-2 heatmap-metric-toggle" role="group" aria-label="Heatmap metric">
              <button
                type="button"
                className={`btn btn-${heatmapMetric === "units" ? "primary" : "outline-primary"}`}
                onClick={() => setHeatmapMetric("units")}
              >
                Units
              </button>
              <button
                type="button"
                className={`btn btn-${heatmapMetric === "amount" ? "primary" : "outline-primary"}`}
                onClick={() => setHeatmapMetric("amount")}
              >
                Amount
              </button>
            </div>
          </div>

          <div className="text-muted small" style={{ marginTop: 6 }}>
            {appliedUpcs.length ? `${appliedUpcs.length} UPC(s) filtered` : "All products"}
          </div>
        </div>

        <div className="px-4 pb-4">
          {loading_orders_units_heatmap_by_state ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : orders_units_heatmap_by_state_error ? (
            <div className="text-danger text-center py-5">{orders_units_heatmap_by_state_error}</div>
          ) : !orders_units_heatmap_by_state?.length ? (
            <div className="text-center py-5">No data for this selection.</div>
          ) : (
            <USStateUnitsChoroplethMap
              data={orders_units_heatmap_by_state}
              themeColor={themeColor}
              height={520}
              metric={heatmapMetric}
              // NEW: click => open modal (no impact if user doesn't click)
              onStateClick={handleStateClick}
            />
          )}
        </div>
      </Card>

      {/* ===================== */}
      {/* NEW: Drilldown Modal  */}
      {/* ===================== */}
      <Modal show={showStateModal} onHide={closeStateModal} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{selectedState ? `State Drilldown: ${selectedState}` : "State Drilldown"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!selectedStateRow ? (
            <div className="text-muted text-center py-4">No drilldown data available for this state.</div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="text-muted small">Totals</div>
                <div className="small">
                  <span className="me-3">
                    <b>Units:</b> {Number(selectedStateRow.totalUnits || 0).toLocaleString()}
                  </span>
                  <span>
                    <b>Amount:</b> {formatMoney(selectedStateRow.totalAmount)}
                  </span>
                </div>
              </div>

              {/* City summary table */}
              {/* <div className="table-responsive mb-3">
                <table className="table table-sm table-striped mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: "44%" }}>City</th>
                      <th className="text-end">Units</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCities.length ? (
                      sortedCities.map((c, idx) => (
                        <tr key={`${c?.city || "city"}-${idx}`}>
                          <td>{c?.city || "-"}</td>
                          <td className="text-end">{Number(c?.totalUnits || 0).toLocaleString()}</td>
                          <td className="text-end">{formatMoney(c?.totalAmount)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="text-center text-muted py-3">
                          No cities found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div> */}

              {/* Zip breakdown per city (accordion) */}
              <div className="accordion" id="zipAccordionState">
                {sortedCities.map((c, idx) => {
                  const accId = `zipacc-${selectedState || "ST"}-${idx}`;
                  const headingId = `${accId}-heading`;
                  const collapseId = `${accId}-collapse`;

                  const zips = Array.isArray(c?.zips) ? [...c.zips] : [];
                  zips.sort((a, b) => Number(b?.totalAmount || 0) - Number(a?.totalAmount || 0));

                  return (
                    <div className="accordion-item" key={accId}>
                      <h2 className="accordion-header" id={headingId}>
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#${collapseId}`}
                          aria-expanded="false"
                          aria-controls={collapseId}
                        >
                          <div className="w-100 d-flex justify-content-between">
                            <span>{c?.city || "-"}</span>
                            <span className="text-muted small">
                              {Number(c?.totalUnits || 0).toLocaleString()} units · {formatMoney(c?.totalAmount)}
                            </span>
                          </div>
                        </button>
                      </h2>

                      <div
                        id={collapseId}
                        className="accordion-collapse collapse"
                        aria-labelledby={headingId}
                        data-bs-parent="#zipAccordionState"
                      >
                        <div className="accordion-body">
                          <div className="table-responsive">
                            <table className="table table-sm mb-0">
                              <thead>
                                <tr>
                                  <th style={{ width: "40%" }}>Zip Code</th>
                                  <th className="text-end">Units</th>
                                  <th className="text-end">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {zips.length ? (
                                  zips.map((z, zIdx) => (
                                    <tr key={`${c?.city || "city"}-${z?.zip || "zip"}-${zIdx}`}>
                                      <td>{z?.zip || "-"}</td>
                                      <td className="text-end">{Number(z?.totalUnits || 0).toLocaleString()}</td>
                                      <td className="text-end">{formatMoney(z?.totalAmount)}</td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan={3} className="text-center text-muted py-2">
                                      No zip rows for this city.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

            </>
          )}
        </Modal.Body>

        <Modal.Footer>
            <div className="text-muted small me-auto">
              Tip: click a city row in the accordion to expand zip-level details.
            </div>
          <Button variant="secondary" onClick={closeStateModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrdersUnitsHeatmapByStateMap;
